import { z } from 'zod';
import { ActivationId } from './activation.ts';
import { EntityRef, Id, IdempotencyKey, IsoDateTime, JsonObject, TenantId } from './common.ts';
import { DeadlineBreach, DeadlineWarningCode } from './deadline.ts';
import { ResourceScope } from './entitlement.ts';
import { Hold } from './hold.ts';
import { ActivitySkip, ProcessName } from './process.ts';
import { CaseOutcome, ResolutionState } from './resolution.ts';
import { AnsweredQuestion, EpisodeOutcome, TaskState } from './task.ts';

/**
 * 17a (ADR-015): a Case is the business coordination record. It links many process instances, tasks and
 * versioned entity/artefact references (POs, receipts, invoices, payments live in their owning systems or pack
 * stores; the case carries typed references only). The case and its timeline are projections: the authoritative
 * execution state is each process instance's workflow run. Every projection write carries a stable `commandId`
 * and an expected `revision`.
 */

export const CaseState = z
  .enum(['open', 'in_progress', 'on_hold', 'cancelling', 'closed', 'cancelled'])
  .meta({ id: 'CaseState' });
export type CaseState = z.infer<typeof CaseState>;

/**
 * Legal case transitions. Closed and cancelled cases are final: follow-up work (warranty claim, renewal) opens a
 * new case linked with relation `follows`. `cancelling` (added by 17b2, ADR-015 §7 amendment) holds while a
 * cancelled process still has non-terminal children or uncertain business operations; the case is never shown as
 * cleanly `cancelled` while effects are unknown.
 */
export const CASE_TRANSITIONS: Readonly<Record<CaseState, readonly CaseState[]>> = {
  open: ['in_progress', 'on_hold', 'cancelling', 'closed', 'cancelled'],
  in_progress: ['on_hold', 'cancelling', 'closed', 'cancelled'],
  on_hold: ['in_progress', 'cancelling', 'closed', 'cancelled'],
  cancelling: ['cancelled', 'closed'],
  closed: [],
  cancelled: [],
};

export function isLegalCaseTransition(from: CaseState, to: CaseState): boolean {
  return CASE_TRANSITIONS[from].includes(to);
}

/** Link relation, declared by packs (core never interprets it), e.g. `purchase-order`, `invoice`, `follows`. */
export const CaseRelation = z
  .string()
  .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9][a-z0-9-]*)*$/, 'relation must be dotted lowercase kebab case')
  .meta({ id: 'CaseRelation' });
export type CaseRelation = z.infer<typeof CaseRelation>;

/** Platform relation for a case that continues an earlier case (target `{ type: 'case', id }`). */
export const CASE_RELATION_FOLLOWS = 'follows';

/** A typed, versioned reference from a case to an entity, artefact or another case. */
export const CaseLink = z
  .object({
    relation: CaseRelation,
    ref: EntityRef,
    /** Version of the referenced record at link time (e.g. PO revision, ETag). */
    version: z.string().min(1).optional(),
    /** Pack-declared role of the record in the case, e.g. `primary`, `split-delivery`. */
    role: z.string().min(1).optional(),
    addedAt: IsoDateTime,
    addedBy: z.string().min(1),
    /** Which activation produced the link (provenance). */
    source: z
      .object({
        processInstanceId: Id.optional(),
        activationId: ActivationId.optional(),
        taskId: Id.optional(),
      })
      .optional(),
  })
  .meta({ id: 'CaseLink' });
export type CaseLink = z.infer<typeof CaseLink>;

/** Identity of a link: relation + ref (+ version). Re-adding the same identity is a no-op. */
export function caseLinkKey(link: Pick<CaseLink, 'relation' | 'ref' | 'version'>): string {
  return `${link.relation}|${link.ref.type}|${link.ref.id}|${link.version ?? ''}`;
}

export const Case = z
  .object({
    caseId: Id,
    tenantId: TenantId,
    /** Process definition name that opened the case (e.g. `p2p.procure-to-pay`). */
    processType: ProcessName,
    title: z.string().min(1).max(300).optional(),
    /** Resource attributes, one value per pack-declared dimension. */
    scope: ResourceScope,
    state: CaseState,
    links: z.array(CaseLink).default([]),
    processInstanceIds: z.array(Id).default([]),
    /**
     * Pack business keys the case answers to (e.g. `contract:C-77`), unique per tenant among open cases; used by
     * event correlation and by follow-up cases to find their predecessor (`CaseStore.findByBusinessKey`).
     */
    businessKeys: z.array(z.string().min(1).max(255)).default([]),
    createdBy: z.string().min(1),
    idempotencyKey: IdempotencyKey.optional(),
    /** Projection revision; incremented by every applied command. */
    revision: z.number().int().nonnegative(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    closedAt: IsoDateTime.optional(),
    /** Added by 21 step 0 (§1b.3): the open hold while `state` is `on_hold` (reason, owner, resume target). */
    hold: Hold.optional(),
    /**
     * Added by 26a (brief 26 §3.3): the business outcome — resolution state, display state (`caseDisplayStateFor`)
     * and the earliest-due open obligation. List rows carry it so no detail call is needed. Absent on pre-26 rows.
     */
    outcome: CaseOutcome.optional(),
  })
  .meta({ id: 'Case' });
export type Case = z.infer<typeof Case>;

export const CaseTimelineKind = z
  .enum(['case', 'process', 'activity', 'task', 'approval', 'operation', 'inbound', 'note'])
  .meta({ id: 'CaseTimelineKind' });
export type CaseTimelineKind = z.infer<typeof CaseTimelineKind>;

/** Added by 26a (brief 26 §3.3): what an obligation timeline entry records. */
export const CaseTimelineObligationAction = z
  .enum([
    'created',
    'asked',
    'reminded',
    'answered',
    'revised',
    'waived',
    'overdue',
    'escalated',
    'undeliverable',
    'ignored',
  ])
  .meta({ id: 'CaseTimelineObligationAction' });
export type CaseTimelineObligationAction = z.infer<typeof CaseTimelineObligationAction>;

/**
 * One entry of the aggregated case timeline (projection). `commandId` makes appends idempotent. `scope` and
 * `aclSubjects` restrict who may read the entry (the read path checks `view` entitlement on `scope`; personnel or
 * investigation evidence carries a narrower scope than the case).
 */
export const CaseTimelineEntry = z
  .object({
    entryId: Id,
    caseId: Id,
    at: IsoDateTime,
    kind: CaseTimelineKind,
    title: z.string().min(1),
    detail: z.string().optional(),
    actor: z.string().optional(),
    source: z
      .object({
        processInstanceId: Id.optional(),
        activationId: ActivationId.optional(),
        taskId: Id.optional(),
        approvalRequestId: Id.optional(),
        operationId: Id.optional(),
        inboundEventId: z.string().optional(),
      })
      .default({}),
    scope: ResourceScope.optional(),
    aclSubjects: z.array(z.string().min(1)).optional(),
    commandId: z.string().min(1),
    data: JsonObject.optional(),
    /** Added by 20a (`activity` entries of an agent activity): the episode's task (also in `source.taskId`). */
    taskId: Id.optional(),
    /** Added by 20a: the task state the entry records (state entries of an agent activity). */
    taskState: TaskState.optional(),
    /** Added by 20a: terminal episode outcome (kind, reason, open questions, answers given). */
    outcome: EpisodeOutcome.optional(),
    /** Added by 20a: a clarification round — the questions asked and, once answered, the answers. */
    clarification: z
      .object({
        questions: z.array(z.string().min(1)).default([]),
        answers: z.array(AnsweredQuestion).optional(),
      })
      .optional(),
    /** Added by 20a: after a non-succeeded activity, whether the process continued or stopped. */
    processContinued: z.boolean().optional(),
    /** Added by 21 step 0 (§1b.3): a hold or resume entry — who, why, evidence. */
    hold: z
      .object({
        action: z.enum(['hold', 'resume']),
        holdId: Id,
        reason: z.string().min(1).optional(),
        evidence: z.array(z.string().min(1)).optional(),
      })
      .optional(),
    /** Added by 21 step 0 (§1b.2): the skip record of a `skipped` activity entry. */
    skip: ActivitySkip.optional(),
    /** Added by 21 step 0 (§1b.3): `deadline_at_risk` / `breached_while_held` warnings and breaches. */
    deadline: z
      .object({ warning: DeadlineWarningCode.optional(), breach: DeadlineBreach.optional() })
      .optional(),
    /** Added by 26a (brief 26 §3.3, §6.8): an obligation entry — what happened to it and the notification sent. */
    obligation: z
      .object({
        obligationId: Id,
        action: CaseTimelineObligationAction,
        notificationId: z.string().min(1).optional(),
      })
      .optional(),
    /** Added by 26a (brief 26 §3.3): an outcome change. */
    resolution: z.object({ from: ResolutionState, to: ResolutionState }).optional(),
  })
  .meta({ id: 'CaseTimelineEntry' });
export type CaseTimelineEntry = z.infer<typeof CaseTimelineEntry>;

// Events -----------------------------------------------------------------------------------------

export const CASE_EVENT_TYPES = {
  created: 'case.created',
  stateChanged: 'case.state.changed',
  linked: 'case.linked',
  timelineAppended: 'case.timeline.appended',
  /** Added by 21 step 0. */
  held: 'case.held',
  /** Added by 21 step 0. */
  resumed: 'case.resumed',
} as const;

/** `case.created`. */
export const CaseCreatedData = z
  .object({
    caseId: Id,
    tenantId: TenantId,
    processType: ProcessName,
    title: z.string().min(1).max(300).optional(),
    scope: ResourceScope,
    businessKeys: z.array(z.string().min(1)).default([]),
    createdBy: z.string().min(1),
    /** Run command that created the case (`projectionCommandId(runId, stepName)`) or the trigger dedupe key. */
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'CaseCreatedData' });
export type CaseCreatedData = z.infer<typeof CaseCreatedData>;

/** `case.state.changed`. */
export const CaseStateChangedData = z
  .object({
    caseId: Id,
    from: CaseState,
    to: CaseState,
    revision: z.number().int().nonnegative(),
    commandId: z.string().min(1),
    by: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'CaseStateChangedData' });
export type CaseStateChangedData = z.infer<typeof CaseStateChangedData>;

/** `case.linked`. */
export const CaseLinkedData = z
  .object({
    caseId: Id,
    link: CaseLink,
    revision: z.number().int().nonnegative(),
    commandId: z.string().min(1),
  })
  .meta({ id: 'CaseLinkedData' });
export type CaseLinkedData = z.infer<typeof CaseLinkedData>;

/** `case.timeline.appended`. */
export const CaseTimelineAppendedData = CaseTimelineEntry;
export type CaseTimelineAppendedData = z.infer<typeof CaseTimelineAppendedData>;

/** Added by 21 step 0: `case.held` / `case.resumed` (principal, reason, evidence and resume target in `hold`). */
export const CaseHoldEventData = z
  .object({
    caseId: Id,
    processInstanceId: Id.optional(),
    hold: Hold,
    revision: z.number().int().nonnegative(),
    commandId: z.string().min(1),
    by: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'CaseHoldEventData' });
export type CaseHoldEventData = z.infer<typeof CaseHoldEventData>;
